/** Compact summaries are plain text and must satisfy the display contract intact. */
export const isCompactSummary = (value: unknown): value is string =>
  typeof value === 'string' && Boolean(value.trim()) && value.length <= 240 &&
  value.trim().split(/\s+/u).length <= 10 && !/[<>\n\r]/u.test(value);

export const isExecutiveChildId = (id: string): boolean => /^exec__sub_(?:[a-f0-9]{24}|[a-f0-9]{32})$/.test(id);

/**
 * The Executive tab's contract with the backend (executive on Temporal).
 * Backend lanes match THESE shapes; every field is snake_case on the wire.
 * Parsers below are tolerant (a missing field reads as "not reported"), never
 * inventive: nothing here fills in a status the server did not send.
 *
 * ── GET /api/executive ─────────────────────────────────────── ExecutiveState
 *   conversation_id, exists, updated_at, current_turn   (unchanged)
 *   enabled: boolean            false → the tab says "Executive is off for this
 *                               account" and nothing runs for exec__ there
 *                               (account switch off, or env break-glass off as
 *                               in dev2/dev3).
 *   enabled_reason: string|null why, in words: "prod: on for every
 *                               account", "dev2 has no executive worker".
 *                               Shown, so the rollout is never dark. The
 *                               switch is env-wide (no per-account opt-in);
 *                               only this tab is staff-only.
 *   workflow: ExecutiveWorkflowStatus|null   Describe + memo; null when off or
 *                               when Describe failed (then workflow_error).
 *   workflow_error: string|null Describe/bridge failure text → "Executive
 *                               unavailable: <text>".
 *   failures: ExecutiveFailure[] unresolved visible failures, newest first.
 *                               A six-hour trigger that failed pre-send is
 *                               {kind:"trigger_failed", at, task_id, label,
 *                               reason} → "Executive can't reach Temporal since
 *                               HH:MM — <label> for task <task_id> not
 *                               delivered". No mirror, no retry.
 *                               {kind:"passes_paused"} (no customer use in
 *                               14 days, billing, demo or unreadable state
 *                               stops the six-hour pass) and {kind:
 *                               "pass_overdue"} (passes for a day, none wrote
 *                               next_workflows) carry their why in reason.
 *
 * ── GET /api/executive/subagents ───────────── { subagents: ExecutiveSubAgent[] }
 *   Running children plus the last 20 closed, newest first. status is one of
 *   working | stalled | waiting | done | failed; the client also marks a
 *   `working` row stalled when last_progress_at is older than 90s.
 *
 * ── POST /api/new-conversations/exec__main/stream ──────────────── a steer
 *   Account on: bridge.store_steer (signal-with-start), then the
 *   SSE tail of exec__main. The FIRST event is the ack:
 *     {type:"executive_steer", msg_id, mode:"stored"|"queued"|"landed_in_turn"|"duplicate",
 *      ahead_of_triggers?:number}
 *   A stored receipt proves durable ingress only, with no worker admission or
 *   trigger position. Stored and mid-turn receipts read only this ack and close; the running turn's own
 *   tail carries the rest (and `inbox_delivered` naming msg_id when drained).
 *   Refusals (JSON body {error, code}):
 *     503 code "executive_unavailable"  error "Executive unavailable: <reason>"
 *     409 code "executive_off"          error "Executive is off for this account"
 *   No fallback path and no client retry on either.
 *
 * ── POST /api/new-conversations/exec__main/cancel ─────────────────── Stop
 *   bridge.cancel → the running activation ends as an interrupted card with
 *   cause "stopped". A failure is an HTTP error with {error}; it is shown.
 *
 * ── Saved transcript: harness cards ────────────────────────────────────────
 *   A harness message ({role:"user", origin:"harness", is_hidden:true}) may
 *   carry `executive_card: ExecutiveCard`, rendered as a card; its model text
 *   is never shown. Kinds: interrupted | triggered | spawn_refused |
 *   model_failover (see the types below).
 */

/**
 * The existing profile a turn runs under — the executive has no permission
 * system of its own: `chat` (a staff steer), `overview_groom` (every trigger:
 * the six-hour pass's profile), `fanout_child` (a sub-agent: a fan-out
 * workstream). Unknown values are shown as sent, never guessed.
 */
export type ExecutiveProfile = 'chat' | 'overview_groom' | 'fanout_child' | string;

export interface ExecutiveTurn {
  turn_id: string | null;
  status: string | null;
}

/** Per-account counts and their ceilings, counted in workflow state; a per-day limit of 0 means none. */
export interface ExecutiveBudgets {
  activations_today: number;
  activations_per_day: number;
  spawns_today: number;
  spawns_per_day: number;
  spawns_per_turn: number;
}

export interface ExecutiveInFlight {
  activation_id: string;
  step: number;
  /** open | model | execute | ops | verify | finalize */
  kind: string;
  profile: ExecutiveProfile | null;
  started_at: string | null;
  activity_state?: 'queued' | 'started';
  scheduled_at?: string | null;
  last_heartbeat_at?: string | null;
}

export interface ExecutiveSteerOutcome {
  status: 'queued' | 'landed' | 'read' | 'closed' | 'rejected' | 'unknown';
  activation_id: string | null;
  updated_at: string | null;
  outcome: string | null;
  reason: string | null;
}

export interface ExecutiveWorkflowStatus {
  status: 'idle' | 'running' | 'stuck';
  in_flight: ExecutiveInFlight | null;
  /** Steers + triggers waiting for the next activation. */
  inbox_depth: number;
  /** msg_ids of steers still waiting in workflow state. */
  queued_steer_ids: string[];
  steer_outcomes?: Record<string, ExecutiveSteerOutcome>;
  steer_outcomes_limit?: number;
  budgets: ExecutiveBudgets | null;
  /** A workflow task failing repeatedly (pending_workflow_task.attempt + last failure). */
  stuck: { attempt: number; error: string } | null;
  /** Queued work and no turn running it (the server says why); a stuck task is `stuck` instead. */
  stalled?: string | null;
}

export interface ExecutiveFailure {
  kind: 'trigger_failed' | string;
  /** When it first failed (ISO). */
  at: string;
  task_id: string | null;
  /** What was not delivered, e.g. "6-hour pass". */
  label: string;
  reason: string;
}

export interface ExecutiveState {
  conversation_id: string;
  exists: boolean;
  updated_at: string | null;
  current_turn: ExecutiveTurn | null;
  enabled: boolean;
  enabled_reason: string | null;
  workflow: ExecutiveWorkflowStatus | null;
  workflow_error: string | null;
  failures: ExecutiveFailure[];
  /** True only once permanent archive and activity projection are activated for this account. */
  activity_feed_ready?: boolean;
}

export type SubAgentStatus = 'working' | 'stalled' | 'waiting' | 'done' | 'failed';

export interface ExecutiveSubAgent {
  /** exec__sub_<24 or 32 hex>: workflow id == conversation id. */
  id: string;
  name: string;
  profile: ExecutiveProfile | null;
  status: SubAgentStatus;
  started_at: string | null;
  closed_at: string | null;
  /** Last heartbeat or step boundary (ISO). */
  last_progress_at: string | null;
  progress: { step: number; label: string | null; activity_state?: 'queued' | 'started' } | null;
  /** failed: the cause in words, e.g. "interrupted · worker deploy". */
  failure: string | null;
  /** done: the one-line report. */
  summary: string | null;
}

export interface ExecutiveStep {
  step: number;
  kind: string;
  code: string | null;
  /** Code over 64KB lives behind a pointer; the card names it rather than showing it. */
  code_ptr: string | null;
}

export interface ExecutiveTriggerItem {
  /** pass | report | review | wake | … */
  kind: string;
  label: string;
  /** Deliveries merged into this activation (latest text wins). */
  count: number;
  /** Repeats of a key already consumed earlier: dropped, shown as handled. */
  already_handled: number;
  child_id: string | null;
}

export interface ExecutiveResultArtifact {
  type: string;
  id: string;
  name: string;
  platform?: string;
}

function parseExecutiveArtifacts(value: unknown, limit: number): ExecutiveResultArtifact[] {
  return list(value).slice(0, limit).flatMap(raw => {
    const artifact = obj(raw);
    const identity = str(artifact?.id);
    const type = str(artifact?.type);
    const platform = str(artifact?.platform);
    if (!identity || !/^[A-Za-z0-9_.:-]{1,128}$/.test(identity) || !type) return [];
    return [{ type, id: identity, name: (str(artifact?.name) ?? identity).slice(0, 300),
      platform: platform && /^[A-Za-z0-9_-]{1,64}$/.test(platform) ? platform : undefined }];
  });
}

export interface ExecutiveChildResult {
  available: boolean;
  text: string;
  artifacts: ExecutiveResultArtifact[];
  status?: 'done' | 'failed' | 'interrupted' | 'stopped';
}

/** A persisted activity projection; opening this feed never asks the browser to summarize. */
export const EXECUTIVE_FEED_MAX_PAGES = 5;

export interface ExecutiveFeedRow {
  id: string;
  timestamp: string | null;
  event_kind: 'message' | 'alert' | 'report' | 'scheduled' | 'system';
  event_summary: string | null;
  event_text: string;
  outcome_summary: string | null;
  outcome_text: string | null;
  status: 'pending' | 'ready' | 'failed' | 'interrupted' | 'needs_attention' | 'unavailable';
  artifacts: ExecutiveResultArtifact[];
  child_ids: string[];
  revision: string;
}

export interface ExecutiveFeedPage {
  schema_version: 1;
  conversation_id: 'exec__main';
  rows: ExecutiveFeedRow[];
  next_cursor: string | null;
  generated_at: string | null;
  coverage: { state: 'ready' | 'warming' | 'unavailable' };
}

/** Reject malformed/oversized pages rather than silently skipping an activity. */
export function parseExecutiveFeed(v: unknown): ExecutiveFeedPage | null {
  const raw = obj(v);
  const coverage = obj(raw?.coverage);
  if (!raw || raw.schema_version !== 1 || raw.conversation_id !== 'exec__main' ||
      !coverage || !['ready', 'warming', 'unavailable'].includes(String(coverage.state)) ||
      !Array.isArray(raw.rows) || raw.rows.length > 50 ||
      !(raw.next_cursor === null || typeof raw.next_cursor === 'string' && raw.next_cursor.length > 0 && raw.next_cursor.length <= 4096)) return null;
  const seen = new Set<string>();
  const rows: ExecutiveFeedRow[] = [];
  for (const value of raw.rows) {
    const row = obj(value);
    const id = str(row?.id);
    const revision = typeof row?.revision === 'number' && Number.isSafeInteger(row.revision) && row.revision >= 0
      ? String(row.revision) : str(row?.revision);
    if (!row || !id || id.length > 200 || seen.has(id) || !revision || revision.length > 200 ||
        typeof row.event_text !== 'string' || row.event_text.length > 6000 ||
        !['message', 'alert', 'report', 'scheduled', 'system'].includes(String(row.event_kind)) ||
        !['pending', 'ready', 'failed', 'interrupted', 'needs_attention', 'unavailable'].includes(String(row.status)) ||
        !(row.timestamp === null || typeof row.timestamp === 'string' && Number.isFinite(Date.parse(row.timestamp)))) return null;
    if (![row.event_summary, row.outcome_summary].every(summary => summary == null || isCompactSummary(summary))) return null;
    seen.add(id);
    const artifacts = parseExecutiveArtifacts(row.artifacts, 12);
    rows.push({ id, revision, timestamp: row.timestamp as string | null,
      event_kind: row.event_kind as ExecutiveFeedRow['event_kind'], event_text: row.event_text,
      event_summary: typeof row.event_summary === 'string' ? row.event_summary.trim() : null,
      outcome_summary: typeof row.outcome_summary === 'string' ? row.outcome_summary.trim() : null,
      outcome_text: typeof row.outcome_text === 'string' ? row.outcome_text.slice(0, 200) : null,
      status: row.status as ExecutiveFeedRow['status'], artifacts,
      child_ids: list(row.child_ids).filter((child): child is string => typeof child === 'string' && isExecutiveChildId(child)).slice(0, 12) });
  }
  return { schema_version: 1, conversation_id: 'exec__main', rows,
    next_cursor: raw.next_cursor as string | null, generated_at: str(raw.generated_at),
    coverage: { state: coverage.state as ExecutiveFeedPage['coverage']['state'] } };
}

export function parseChildResults(v: unknown): Record<string, ExecutiveChildResult> {
  const results = obj(obj(v)?.results);
  if (!results) return {};
  return Object.fromEntries(Object.entries(results).flatMap(([id, value]) => {
    const result = obj(value);
    if (!isExecutiveChildId(id) || !result ||
        typeof result.available !== 'boolean' || typeof result.text !== 'string' || result.text.length > 16_000) return [];
    const artifacts = parseExecutiveArtifacts(result.artifacts, 8);
    const status = ['done', 'failed', 'interrupted', 'stopped'].includes(String(result.status))
      ? result.status as ExecutiveChildResult['status'] : undefined;
    return [[id, { available: result.available, text: result.text, artifacts, ...(status ? { status } : {}) }]];
  }));
}

export interface ExecutiveInterruptedCard {
  kind: 'interrupted';
  activation_id: string;
  /** stopped | worker_lost | deploy | timeout | context_full | error */
  cause: string;
  stopped_by: string | null;
  detail: string | null;
  profile: ExecutiveProfile | null;
  completed: ExecutiveStep[];
  in_flight: ExecutiveStep | null;
}

export interface ExecutiveTriggeredCard {
  kind: 'triggered';
  activation_id: string;
  profile: ExecutiveProfile | null;
  items: ExecutiveTriggerItem[];
  budgets: ExecutiveBudgets | null;
}

export interface ExecutiveSpawnRefusedCard {
  kind: 'spawn_refused';
  activation_id: string;
  name: string;
  reason: string;
}

export interface ExecutiveModelFailoverCard {
  kind: 'model_failover';
  activation_id: string;
  failovers: Array<{ from: string; to: string; reason: string }>;
}

export type ExecutiveCard =
  | ExecutiveInterruptedCard
  | ExecutiveTriggeredCard
  | ExecutiveSpawnRefusedCard
  | ExecutiveModelFailoverCard;

/** The steer ack (first /stream event), plus when this client received it. */
export interface ExecutiveSteerAck {
  msgId: string;
  mode: 'stored' | 'queued' | 'landed_in_turn' | 'duplicate';
  aheadOfTriggers: number;
  at: number;
}

/** A refusal the tab shows as-is: no retry offered. */
export interface ExecutiveNotice {
  kind: 'unavailable' | 'off' | 'stop_failed' | 'refused';
  text: string;
}

/** Poll cadence for both GET endpoints: every 5s while the page is visible, every 30s while hidden. */
export const EXECUTIVE_POLL_VISIBLE_MS = 5_000;
export const EXECUTIVE_POLL_HIDDEN_MS = 30_000;
export const pollIntervalMs = (visibility: string | undefined): number =>
  visibility === 'hidden' ? EXECUTIVE_POLL_HIDDEN_MS : EXECUTIVE_POLL_VISIBLE_MS;

export const EXECUTIVE_UNAVAILABLE_CODE = 'executive_unavailable';
export const EXECUTIVE_OFF_CODE = 'executive_off';
export const EXECUTIVE_OFF_TEXT = 'Executive is off for this account';
export const EXECUTIVE_UNAVAILABLE_PREFIX = 'Executive unavailable';

export const isExecutiveConversationId = (id: string | null | undefined): boolean =>
  typeof id === 'string' && id.startsWith('exec__');

type Raw = Record<string, unknown>;
const obj = (v: unknown): Raw | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const profile = (v: unknown): ExecutiveProfile | null => str(v);
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

export interface ExecutiveActivityMetrics {
  generated_at: string | null;
  window_days: number;
  handled_events: number | null;
  reports_delivered: number | null;
  recorded_tokens: number | null;
  token_window_days: number;
  estimated_hours: number | null;
  hours_scope: string | null;
}

export function parseExecutiveActivity(v: unknown): ExecutiveActivityMetrics | null {
  const raw = obj(v);
  if (!raw || raw.window_days !== 7 || raw.token_window_days !== 7) return null;
  const count = (key: string): number | null => typeof raw[key] === 'number' &&
    Number.isSafeInteger(raw[key]) && (raw[key] as number) >= 0 ? raw[key] as number : null;
  return { generated_at: str(raw.generated_at), window_days: 7, token_window_days: 7,
    handled_events: count('handled_events'), reports_delivered: count('reports_delivered'), recorded_tokens: count('recorded_tokens'),
    estimated_hours: typeof raw.estimated_hours === 'number' && Number.isFinite(raw.estimated_hours) && raw.estimated_hours >= 0
      ? raw.estimated_hours : null, hours_scope: str(raw.hours_scope) };
}

/** The first /stream event of an executive steer, or null for any other event. */
export function parseSteerAck(chunk: unknown, receivedAt: number): ExecutiveSteerAck | null {
  const c = obj(chunk);
  if (!c || c.type !== 'executive_steer') return null;
  const msgId = str(c.msg_id);
  if (!msgId || (c.mode !== 'stored' && c.mode !== 'queued' && c.mode !== 'landed_in_turn' && c.mode !== 'duplicate')) return null;
  return { msgId, mode: c.mode, aheadOfTriggers: num(c.ahead_of_triggers), at: receivedAt };
}

/** A /stream refusal that is the executive's own (503 unavailable, 409 off), or null. */
export function executiveSendRefusal(error: unknown): ExecutiveNotice | null {
  if (!(error instanceof Error)) return null;
  const { status, code } = error as Error & { status?: number; code?: string };
  if (code === EXECUTIVE_OFF_CODE) return { kind: 'off', text: EXECUTIVE_OFF_TEXT };
  if (code !== EXECUTIVE_UNAVAILABLE_CODE && status !== 503) return null;
  const message = error.message.trim();
  return {
    kind: 'unavailable',
    text: message.startsWith(EXECUTIVE_UNAVAILABLE_PREFIX) ? message : `${EXECUTIVE_UNAVAILABLE_PREFIX}: ${message}`,
  };
}

function parseBudgets(v: unknown): ExecutiveBudgets | null {
  const b = obj(v);
  if (!b) return null;
  return {
    activations_today: num(b.activations_today),
    activations_per_day: num(b.activations_per_day),
    spawns_today: num(b.spawns_today),
    spawns_per_day: num(b.spawns_per_day),
    spawns_per_turn: num(b.spawns_per_turn),
  };
}

function parseWorkflow(v: unknown): ExecutiveWorkflowStatus | null {
  const w = obj(v);
  if (!w) return null;
  const inFlight = obj(w.in_flight);
  const stuck = obj(w.stuck);
  return {
    status: w.status === 'running' || w.status === 'stuck' ? w.status : 'idle',
    in_flight: inFlight
      ? {
          activation_id: str(inFlight.activation_id) ?? '',
          step: num(inFlight.step),
          kind: str(inFlight.kind) ?? 'step',
          profile: profile(inFlight.profile),
          started_at: str(inFlight.started_at),
          ...(inFlight.activity_state === 'queued' || inFlight.activity_state === 'started' ? { activity_state: inFlight.activity_state } : {}),
          ...(inFlight.scheduled_at !== undefined ? { scheduled_at: str(inFlight.scheduled_at) } : {}),
          ...(inFlight.last_heartbeat_at !== undefined ? { last_heartbeat_at: str(inFlight.last_heartbeat_at) } : {}),
        }
      : null,
    inbox_depth: num(w.inbox_depth),
    queued_steer_ids: list(w.queued_steer_ids).filter((id): id is string => typeof id === 'string'),
    ...(obj(w.steer_outcomes) ? { steer_outcomes: Object.fromEntries(
      Object.entries(obj(w.steer_outcomes)!).flatMap(([id, raw]) => {
        const entry = obj(raw);
        if (!entry || !['queued', 'landed', 'read', 'closed', 'rejected', 'unknown'].includes(String(entry.status))) return [];
        return [[id, { status: entry.status as ExecutiveSteerOutcome['status'],
          activation_id: str(entry.activation_id), updated_at: str(entry.updated_at),
          outcome: str(entry.outcome), reason: str(entry.reason) }]];
      }),
    ) } : {}),
    ...(w.steer_outcomes_limit !== undefined ? { steer_outcomes_limit: num(w.steer_outcomes_limit) } : {}),
    budgets: parseBudgets(w.budgets),
    stuck: stuck ? { attempt: num(stuck.attempt), error: str(stuck.error) ?? 'unknown error' } : null,
    stalled: str(w.stalled),
  };
}

function parseFailure(v: unknown): ExecutiveFailure | null {
  const f = obj(v);
  const at = f && str(f.at);
  if (!f || !at) return null;
  return {
    kind: str(f.kind) ?? 'trigger_failed',
    at,
    task_id: str(f.task_id),
    label: str(f.label) ?? '6-hour pass',
    reason: str(f.reason) ?? '',
  };
}

export function parseExecutiveState(v: unknown): ExecutiveState | null {
  const s = obj(v);
  const conversationId = s && str(s.conversation_id);
  if (!s || !conversationId) return null;
  const turn = obj(s.current_turn);
  return {
    conversation_id: conversationId,
    exists: Boolean(s.exists),
    updated_at: str(s.updated_at),
    current_turn: turn ? { turn_id: str(turn.turn_id), status: str(turn.status) } : null,
    // A backend that predates the switch reports nothing: the tab behaves as before.
    enabled: s.enabled !== false,
    enabled_reason: str(s.enabled_reason),
    workflow: parseWorkflow(s.workflow),
    workflow_error: str(s.workflow_error),
    failures: list(s.failures).map(parseFailure).filter((f): f is ExecutiveFailure => f !== null),
    activity_feed_ready: s.activity_feed_ready === true,
  };
}

const SUB_AGENT_STATUSES: readonly SubAgentStatus[] = ['working', 'stalled', 'waiting', 'done', 'failed'];

export function parseSubAgents(v: unknown): ExecutiveSubAgent[] {
  const body = obj(v);
  return list(body?.subagents).flatMap((raw): ExecutiveSubAgent[] => {
    const r = obj(raw);
    const id = r && str(r.id);
    if (!r || !id) return [];
    const progress = obj(r.progress);
    const status = SUB_AGENT_STATUSES.find(s => s === r.status);
    return [{
      id,
      name: str(r.name) ?? id,
      profile: profile(r.profile),
      // An unknown status is not guessed into a good one.
      status: status ?? 'failed',
      started_at: str(r.started_at),
      closed_at: str(r.closed_at),
      last_progress_at: str(r.last_progress_at),
      progress: progress ? { step: num(progress.step), label: str(progress.label),
        ...(progress.activity_state === 'queued' || progress.activity_state === 'started' ? { activity_state: progress.activity_state } : {}) } : null,
      failure: str(r.failure) ?? (status ? null : `unknown status ${String(r.status)}`),
      summary: str(r.summary),
    }];
  });
}

function parseStep(v: unknown): ExecutiveStep | null {
  const s = obj(v);
  if (!s) return null;
  return { step: num(s.step), kind: str(s.kind) ?? 'step', code: str(s.code), code_ptr: str(s.code_ptr) };
}

/** A saved harness message's `executive_card`, or undefined when it is not one. */
export function parseExecutiveCard(v: unknown): ExecutiveCard | undefined {
  const c = obj(v);
  if (!c) return undefined;
  const activationId = str(c.activation_id) ?? '';
  switch (c.kind) {
    case 'interrupted':
      return {
        kind: 'interrupted',
        activation_id: activationId,
        cause: str(c.cause) ?? 'error',
        stopped_by: str(c.stopped_by),
        detail: str(c.detail),
        profile: profile(c.profile),
        completed: list(c.completed).map(parseStep).filter((s): s is ExecutiveStep => s !== null),
        in_flight: parseStep(c.in_flight),
      };
    case 'triggered':
      return {
        kind: 'triggered',
        activation_id: activationId,
        profile: profile(c.profile),
        budgets: parseBudgets(c.budgets),
        items: list(c.items).flatMap((raw): ExecutiveTriggerItem[] => {
          const i = obj(raw);
          if (!i) return [];
          return [{
            kind: str(i.kind) ?? 'trigger',
            label: str(i.label) ?? str(i.kind) ?? 'trigger',
            count: num(i.count),
            already_handled: num(i.already_handled),
            child_id: str(i.child_id),
          }];
        }),
      };
    case 'spawn_refused':
      return {
        kind: 'spawn_refused',
        activation_id: activationId,
        name: str(c.name) ?? 'sub-agent',
        reason: str(c.reason) ?? '',
      };
    case 'model_failover':
      return {
        kind: 'model_failover',
        activation_id: activationId,
        failovers: list(c.failovers).flatMap(raw => {
          const f = obj(raw);
          return f ? [{ from: str(f.from) ?? '?', to: str(f.to) ?? '?', reason: str(f.reason) ?? '' }] : [];
        }),
      };
    default:
      return undefined;
  }
}
